import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, USERNAME_PATTERN } from '../config.ts';

/**
 * Validación de formulario. Es SOLO experiencia de uso: sirve para no hacer un viaje al
 * servidor por una errata evidente y para poder señalar el campo concreto.
 *
 * La validación que cuenta es la del servidor (zod en el borde de cada endpoint), y sus
 * reglas son las mismas que estas: si algún día se relajan aquí, el backend seguirá
 * rechazándolo. Nunca al revés. Nada de lo que se comprueba aquí es una medida de
 * seguridad: quien quiera saltárselo solo tiene que no usar este formulario.
 */

/**
 * Comprobación de email deliberadamente laxa: "algo@algo.algo" sin espacios, la misma
 * forma que exige la restricción `users_email_format` de la base. Una expresión regular
 * "completa" de RFC 5322 rechaza direcciones válidas y no aporta nada aquí, porque quien
 * decide de verdad si un email existe es el correo de verificación (aún pendiente).
 */
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type FieldErrors<T extends string> = Partial<Record<T, string>>;

export type LoginField = 'email' | 'password';
export type RegisterField = 'email' | 'username' | 'password' | 'consent';

export function validateLogin(values: {
  email: string;
  password: string;
}): FieldErrors<LoginField> {
  const errors: FieldErrors<LoginField> = {};
  if (values.email.trim() === '') errors.email = 'validation.emailRequired';
  else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'validation.emailInvalid';
  if (values.password === '') errors.password = 'validation.passwordRequired';
  return errors;
}

export function validateRegister(values: {
  email: string;
  username: string;
  password: string;
  consent: boolean;
}): FieldErrors<RegisterField> {
  const errors: FieldErrors<RegisterField> = {};

  const email = values.email.trim();
  if (email === '') errors.email = 'validation.emailRequired';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'validation.emailInvalid';

  const username = values.username.trim();
  if (username === '') errors.username = 'validation.usernameRequired';
  else if (!USERNAME_PATTERN.test(username)) errors.username = 'validation.usernameInvalid';

  if (values.password === '') errors.password = 'validation.passwordRequired';
  else if (values.password.length < PASSWORD_MIN_LENGTH) {
    errors.password = 'validation.passwordTooShort';
  } else if (values.password.length > PASSWORD_MAX_LENGTH) {
    errors.password = 'validation.passwordTooLong';
  }

  // RGPD: sin consentimiento explícito no se envía nada.
  if (!values.consent) errors.consent = 'validation.consentRequired';

  return errors;
}

export function hasErrors(errors: Record<string, string | undefined>): boolean {
  return Object.values(errors).some((value) => value !== undefined);
}
